import React, { useState, useMemo } from 'react';
import { useMeasureReadings } from '../state/data/patient';
import { buildWeightSeries, computeWeightSummary, WEIGHT_TYPES, WEIGHT_MEASTYPES } from '../logic/weightReadings';
import { KG_TO_LB } from '../logic/patientHome';
import { measureWindowYmd, OVERVIEW_SERIES_DAYS } from '../logic/measureWindow';
import useNow from '../hooks/useNow';
import { filedMeasureBaseline, baselineMissingCopy } from '../logic/baseline';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

/* ── Series styling ───────────────────────────────────────────── */

const SERIES = [
  { type: WEIGHT_TYPES.weight,     label: 'Weight',      color: '#2563EB' },
  { type: WEIGHT_TYPES.fatMass,    label: 'Fat mass',    color: '#D97706' },
  { type: WEIGHT_TYPES.muscleMass, label: 'Muscle mass', color: '#16A34A' },
];

function fmtDate(epoch) {
  return new Date(epoch * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Withings reports kg; the program displays lbs.
const toLb = (kg) => kg * KG_TO_LB;

function fmtLb(kg) {
  return kg == null ? '—' : `${toLb(kg).toFixed(1)}`;
}

function fmtPct(pct) {
  return pct == null ? '—' : pct.toFixed(1);
}

/* ── Latest body-composition stat ─────────────────────────────── */

function Stat({ label, value, unit }) {
  return (
    <div>
      <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--wi-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{value}</span>
        {value !== '—' && <span style={{ fontSize: 11, color: 'var(--wi-text-secondary)' }}>{unit}</span>}
      </div>
    </div>
  );
}

/* ── Line chart ───────────────────────────────────────────────── */

function WeightChart({ series, rangeDays, targetWeightKg }) {
  const grid = useMemo(() => {
    const now = new Date();
    now.setHours(12, 0, 0, 0);
    // Per-type day → value lookup.
    const byTypeDay = {};
    for (const { type } of SERIES) {
      byTypeDay[type] = new Map();
      (series[type] || []).forEach((pt) => {
        byTypeDay[type].set(new Date(pt.epoch * 1000).toLocaleDateString('en-CA'), pt.value);
      });
    }

    const rows = [];
    for (let i = rangeDays - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const key = date.toLocaleDateString('en-CA');
      const row = { epoch: Math.floor(date.getTime() / 1000) };
      for (const { type } of SERIES) row[type] = byTypeDay[type].get(key) ?? null;
      rows.push(row);
    }
    return rows;
  }, [series, rangeDays]);

  const datasets = SERIES.map((s) => ({
    label: s.label,
    data: grid.map((r) => (r[s.type] == null ? null : toLb(r[s.type]))),
    borderColor: s.color,
    backgroundColor: s.color,
    borderWidth: 2,
    pointRadius: 0,
    pointHoverRadius: 5,
    tension: 0.3,
    spanGaps: true,
  }));
  if (targetWeightKg != null) {
    // Care-plan weight target (kg → lb), same dashed style as the BP target line.
    datasets.push({
      label: 'Target',
      data: grid.map(() => toLb(targetWeightKg)),
      borderColor: 'rgba(34,197,94,0.35)',
      borderWidth: 1.5,
      borderDash: [6, 4],
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false,
    });
  }
  const data = {
    labels: grid.map((r) => fmtDate(r.epoch)),
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true, position: 'bottom', align: 'start',
        labels: {
          usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 16,
          font: { size: 11, family: 'Aeonik, sans-serif' }, color: '#6B7280',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.9)',
        titleFont: { size: 12, family: 'Aeonik, sans-serif', weight: '500' },
        bodyFont: { size: 12, family: 'Aeonik, sans-serif', weight: '500' },
        padding: { top: 8, bottom: 8, left: 12, right: 12 },
        cornerRadius: 8, displayColors: true, boxWidth: 8, boxHeight: 8,
        callbacks: {
          label: (item) => (item.raw == null ? null : ` ${item.dataset.label}: ${item.raw.toFixed(1)} lb`),
        },
      },
    },
    scales: {
      x: {
        offset: true, // inset first/last points off the axis border (else a recent reading clips at the edge)
        ticks: { font: { size: 10 }, color: '#9CA3AF', maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
        grid: { color: 'rgba(0,0,0,0.03)' },
        border: { display: false },
      },
      y: {
        ticks: { font: { size: 10 }, color: '#9CA3AF', callback: (v) => `${v}` },
        grid: { color: 'rgba(0,0,0,0.04)' },
        border: { display: false },
      },
    },
    interaction: { intersect: false, mode: 'index' },
  };

  return (
    <div style={{ position: 'relative', height: 220 }}>
      <Line data={data} options={options} />
    </div>
  );
}

/* ── Main ─────────────────────────────────────────────────────── */

export default function WeightReadingsCard({ userid, targetWeightKg, cmsSubmissions = null }) {
  const [rangeDays, setRangeDays] = useState(OVERVIEW_SERIES_DAYS);

  const nowMs = useNow();
  const { startdateymd, enddateymd } = useMemo(() => measureWindowYmd(rangeDays, nowMs), [rangeDays, nowMs]);

  const query = useMeasureReadings(userid, WEIGHT_MEASTYPES, { startdateymd, enddateymd });
  const loading = query.isLoading;
  const error = query.error ? (query.error.message || 'Failed to load') : null;
  const rawReadings = query.data?.readings ?? null;

  const series = useMemo(() => (rawReadings ? buildWeightSeries(rawReadings) : {}), [rawReadings]);
  const summary = useMemo(() => (rawReadings ? computeWeightSummary(rawReadings) : null), [rawReadings]);

  if (loading) return (
    <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, padding: '20px', textAlign: 'center', marginTop: 24 }}>
      <span className="detail1 text-secondary">Loading body metrics…</span>
    </div>
  );
  if (error) return (
    <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, padding: '20px', textAlign: 'center', marginTop: 24 }}>
      <span className="detail1 text-secondary">Unable to load readings </span>
      <button onClick={() => query.refetch()} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--wi-action-primary)', fontFamily: 'var(--wi-font-family)', fontWeight: 500 }}>Retry</button>
    </div>
  );
  if (!summary) return (
    <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, padding: '20px', textAlign: 'center', marginTop: 24 }}>
      <span className="detail1 text-secondary">No weight readings in the last {rangeDays} days</span>
    </div>
  );

  const delta = summary.weightDelta;
  // The weight baseline is the one CMS received — WF37 picks its own device reading, so the stored
  // row (WF9's first-reading latch) is a readiness value, not a baseline. logic/baseline.ts owns it.
  const baselineKg = filedMeasureBaseline('weight', cmsSubmissions, userid);
  const deltaColor = delta == null || Math.abs(delta) < 0.1 ? 'var(--wi-text-secondary)' : delta < 0 ? 'var(--wi-status-good)' : 'var(--wi-status-moderate)';
  const deltaIcon = delta == null ? '' : delta < 0 ? '↓' : delta > 0 ? '↑' : '→';

  return (
    <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, border: '1px solid var(--wi-divider)', overflow: 'hidden', marginTop: 24 }}>

      {/* Header + range toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px 0' }}>
        <span className="eyebrow text-secondary">Body Metrics</span>
        <div style={{ display: 'flex', gap: 2, background: 'var(--wi-background-primary)', borderRadius: 6, padding: 2 }}>
          {[30, 90].map((d) => (
            <button key={d} onClick={() => setRangeDays(d)} style={{
              fontSize: 11, fontWeight: rangeDays === d ? 600 : 400,
              padding: '4px 12px', borderRadius: 4, border: 'none', cursor: 'pointer',
              background: rangeDays === d ? 'var(--wi-background-secondary)' : 'transparent',
              color: rangeDays === d ? 'var(--wi-text-primary)' : 'var(--wi-text-secondary)',
              fontFamily: 'var(--wi-font-family)',
              boxShadow: rangeDays === d ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            }}>{d}d</button>
          ))}
        </div>
      </div>

      {/* Summary row */}
      <div style={{ display: 'flex', gap: 28, padding: '14px 20px', borderBottom: '1px solid var(--wi-divider)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--wi-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>
            Weight
          </span>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{fmtLb(summary.lastWeight)}</span>
            <span style={{ fontSize: 12, color: 'var(--wi-text-secondary)' }}>lb</span>
            {delta != null && (
              <span style={{ fontSize: 13, fontWeight: 600, color: deltaColor, marginLeft: 4 }}>
                {deltaIcon} {toLb(Math.abs(delta)).toFixed(1)} lb
              </span>
            )}
          </div>
        </div>
        <Stat label="Fat mass" value={fmtPct(summary.latestFatPct)} unit="%" />
        <Stat label="Muscle mass" value={fmtPct(summary.latestMusclePct)} unit="%" />
        {baselineKg != null ? (
          <Stat label="Baseline" value={fmtLb(baselineKg)} unit="lb" />
        ) : (
          <div>
            <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--wi-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>
              Baseline
            </span>
            <span style={{ fontSize: 12, color: 'var(--wi-text-placeholder)', fontStyle: 'italic' }}>{baselineMissingCopy(cmsSubmissions, userid)}</span>
          </div>
        )}
      </div>

      {/* Chart */}
      {summary.count >= 2 && (
        <div style={{ padding: '8px 16px 4px' }}>
          <WeightChart series={series} rangeDays={rangeDays} targetWeightKg={targetWeightKg} />
        </div>
      )}

    </div>
  );
}
